import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusyOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupSumBy, topN } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import type { AbcAnalysisRow, InventoryValuationRow, LowStockRow, NearExpiryRow } from '@/types/reports';

const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function InventoryPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: valuation = [], isLoading: loadingValuation } = useQuery({
    queryKey: ['reports', 'inventory-valuation'],
    queryFn: async () => (await apiClient.get<InventoryValuationRow[]>('/reports/inventory-valuation')).data,
  });
  const { data: lowStock = [], isLoading: loadingLowStock } = useQuery({
    queryKey: ['reports', 'low-stock'],
    queryFn: async () => (await apiClient.get<LowStockRow[]>('/reports/low-stock')).data,
  });
  const { data: nearExpiry = [] } = useQuery({
    queryKey: ['reports', 'near-expiry'],
    queryFn: async () => (await apiClient.get<NearExpiryRow[]>('/reports/near-expiry', { params: { days: 30 } })).data,
  });
  const { data: abcAnalysis = [], isLoading: loadingAbc } = useQuery({
    queryKey: ['reports', 'abc-analysis'],
    queryFn: async () => (await apiClient.get<AbcAnalysisRow[]>('/reports/abc-analysis')).data,
  });

  const totalStockValue = useMemo(() => valuation.reduce((sum, r) => sum + r.stockValue, 0), [valuation]);

  const topMaterials = useMemo(() => topN(valuation, (r) => r.stockValue, 10).reverse(), [valuation]);
  const byCategory = useMemo(() => topN(groupSumBy(valuation, (r) => r.categoryName, (r) => r.stockValue), (r) => r.total, 8), [valuation]);

  const abcColors = [colors.inventory, colors.production, colors.adjustments];

  const lowStockColumns: DataTableColumn<ReturnType<typeof withId<LowStockRow>>[number]>[] = [
    { field: 'materialCode', headerName: tr('columns.materialCode') },
    { field: 'materialName', headerName: tr('columns.materialName') },
    { field: 'quantityOnHand', headerName: tr('columns.onHand'), align: 'right' },
    { field: 'reorderLevel', headerName: tr('columns.reorderLevel'), align: 'right' },
    { field: 'safetyStock', headerName: tr('columns.safetyStock'), align: 'right' },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inventory.totalStockValue')} value={formatCurrency(totalStockValue)} icon={<PaymentsIcon />} accentColor={colors.inventory} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inventory.skuCount')} value={valuation.length} icon={<Inventory2Icon />} accentColor={colors.inventory} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('lowStock')} value={lowStock.length} icon={<WarningAmberIcon />} accentColor={colors.adjustments} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('nearExpiry')} value={nearExpiry.length} icon={<EventBusyIcon />} accentColor={colors.adjustments} />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={6}>
          <ChartCard
            title={t('inventory.topMaterialsTitle')}
            subtitle={t('inventory.topMaterialsSubtitle')}
            loading={loadingValuation}
            empty={!loadingValuation && topMaterials.length === 0}
            height={360}
          >
            <DashboardBarChart
              layout="horizontal"
              categories={topMaterials.map((r) => r.materialCode)}
              series={[{ label: tr('columns.value'), data: topMaterials.map((r) => r.stockValue), color: colors.inventory }]}
              valueFormatter={(v) => formatCurrency(v ?? 0)}
              height={360}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={6}>
          <ChartCard title={t('inventory.abcTitle')} subtitle={t('inventory.abcSubtitle')} loading={loadingAbc} empty={!loadingAbc && abcAnalysis.length === 0} height={360}>
            <DashboardPieChart
              height={360}
              data={abcAnalysis.map((r, i) => ({ label: `${tr('columns.abcClass')} ${r.abcClass}`, value: r.stockValue, color: abcColors[i % abcColors.length] }))}
              valueFormatter={(item) => formatCurrency(item.value)}
            />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <ChartCard title={t('inventory.byCategoryTitle')} loading={loadingValuation} empty={!loadingValuation && byCategory.length === 0} height={280}>
        <DashboardBarChart
          categories={byCategory.map((r) => r.key)}
          series={[{ label: tr('columns.value'), data: byCategory.map((r) => r.total), color: colors.inventory }]}
          valueFormatter={(v) => formatCurrency(v ?? 0)}
          height={280}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('inventory.lowStockTableTitle')}
        </Typography>
        <DataTable columns={lowStockColumns} rows={withId(lowStock, (r) => r.materialId)} loading={loadingLowStock} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
